/**
 * useTema — Elección de tema de la app (claro / oscuro / automático).
 *
 * Qué hace:
 *   1. Recuerda la elección del usuario en localStorage (clave 'sn_tema').
 *   2. La traduce al tema que realmente se pinta y lo aplica poniendo
 *      data-tema en <html>, que es lo que leen los tokens de styles/temas.css.
 *   3. En 'automatico' sigue la preferencia del sistema (prefers-color-scheme)
 *      y REACCIONA si el usuario la cambia con la app abierta.
 *
 * Por defecto: 'claro'.
 *
 * Nota sobre React.StrictMode (así arranca esta app en index.tsx):
 *   los inicializadores de useState se ejecutan DOS veces. Por eso el
 *   inicializador solo LEE: no escribe en localStorage ni toca el DOM. Guardar
 *   es responsabilidad de cambiarTema() (un manejador de evento) y pintar, de
 *   un efecto.
 *
 * El primer pintado no depende de este hook: public/index.html ya pone
 * data-tema antes de que React monte, para que no haya parpadeo blanco.
 */
import { useCallback, useEffect, useState } from 'react';

/** Lo que el usuario elige en la pantalla de Apariencia. */
export type OpcionTema = 'claro' | 'oscuro' | 'automatico';

/** Lo que finalmente se pinta ('automatico' ya resuelto). */
export type TemaAplicado = 'claro' | 'oscuro';

/** Clave de localStorage. La comparte el script de arranque de index.html. */
export const CLAVE_TEMA = 'sn_tema';

/** Tema con el que arranca un usuario que nunca eligió nada. */
export const OPCION_POR_DEFECTO: OpcionTema = 'claro';

const CONSULTA_OSCURO = '(prefers-color-scheme: dark)';

const esOpcionValida = (valor: unknown): valor is OpcionTema =>
  valor === 'claro' || valor === 'oscuro' || valor === 'automatico';

/**
 * Lee la opción guardada. FUNCIÓN PURA: solo lee, no escribe nada.
 * Si el valor guardado está corrupto o el almacenamiento está bloqueado
 * (modo privado, WebView restringida), cae al tema por defecto.
 */
export function leerOpcionGuardada(): OpcionTema {
  try {
    const guardado = localStorage.getItem(CLAVE_TEMA);
    return esOpcionValida(guardado) ? guardado : OPCION_POR_DEFECTO;
  } catch {
    return OPCION_POR_DEFECTO;
  }
}

/* ---------------------------------------------------------------------------
   ESTADO COMPARTIDO

   El hook puede usarse en varias pantallas a la vez (App lo monta siempre y
   Apariencia también). Si cada copia llevara su propio estado, una podría
   pisar a la otra al cambiar la preferencia del sistema. Con esta pizarra
   común todas ven lo mismo.
--------------------------------------------------------------------------- */
let opcionVigente: OpcionTema | undefined;
const escuchas = new Set<(opcion: OpcionTema) => void>();

/**
 * Opción vigente. La primera vez la lee de localStorage; a partir de ahí vive
 * en memoria, para que todas las pantallas coincidan aunque el almacenamiento
 * esté bloqueado. Es idempotente: llamarla dos veces (StrictMode) da igual.
 */
function opcionVigenteActual(): OpcionTema {
  if (opcionVigente === undefined) opcionVigente = leerOpcionGuardada();
  return opcionVigente;
}

/**
 * Preferencia de color del sistema. FUNCIÓN PURA.
 * Si el navegador no soporta matchMedia, asume claro.
 */
export function preferenciaDelSistema(): TemaAplicado {
  try {
    return window.matchMedia(CONSULTA_OSCURO).matches ? 'oscuro' : 'claro';
  } catch {
    return 'claro';
  }
}

/** Traduce la opción elegida al tema que se pinta de verdad. */
export function resolverTema(opcion: OpcionTema, sistema: TemaAplicado): TemaAplicado {
  return opcion === 'automatico' ? sistema : opcion;
}

/**
 * Pone data-tema en <html>. Es el único punto que toca el DOM.
 *
 * También pone data-bs-theme, que es como Bootstrap 5.3 decide su propia
 * paleta. Sin esto, cualquier superficie de Bootstrap que la app no haya
 * repintado a mano (.modal-content, .form-control, .dropdown-menu, .table,
 * .list-group…) se quedaba BLANCA en tema oscuro mientras el texto de dentro
 * heredaba --sn-texto, que en oscuro es casi blanco: texto invisible.
 */
function aplicarTema(tema: TemaAplicado): void {
  const html = document.documentElement;
  html.setAttribute('data-tema', tema);
  html.setAttribute('data-bs-theme', tema === 'oscuro' ? 'dark' : 'light');
}

export type UsoTema = {
  /** Lo que el usuario eligió: 'claro' | 'oscuro' | 'automatico'. */
  opcion: OpcionTema;
  /** Lo que se está pintando ahora mismo: 'claro' | 'oscuro'. */
  temaAplicado: TemaAplicado;
  /** Preferencia actual del sistema (útil para explicar el modo automático). */
  temaDelSistema: TemaAplicado;
  /** Cambia la opción, la aplica y la guarda. */
  cambiarTema: (nueva: OpcionTema) => void;
};

export function useTema(): UsoTema {
  // Inicializadores puros: en StrictMode corren dos veces y no pasa nada.
  const [opcion, setOpcion] = useState<OpcionTema>(opcionVigenteActual);
  const [temaDelSistema, setTemaDelSistema] = useState<TemaAplicado>(preferenciaDelSistema);

  // Se apunta a la pizarra común para enterarse de los cambios hechos desde
  // otras pantallas.
  useEffect(() => {
    setOpcion(opcionVigenteActual()); // por si cambió antes de montar
    escuchas.add(setOpcion);
    return () => { escuchas.delete(setOpcion); };
  }, []);

  // Escucha la preferencia del sistema. Se mantiene siempre activa (no solo en
  // 'automatico') para que, al pasar a automático, el valor ya esté al día.
  useEffect(() => {
    let consulta: MediaQueryList;
    try {
      consulta = window.matchMedia(CONSULTA_OSCURO);
    } catch {
      return; // navegador sin matchMedia: se queda en claro
    }

    const alCambiar = (evento: MediaQueryListEvent) => {
      setTemaDelSistema(evento.matches ? 'oscuro' : 'claro');
    };

    // Sincroniza por si el sistema cambió entre el primer render y este efecto.
    setTemaDelSistema(consulta.matches ? 'oscuro' : 'claro');

    // addEventListener es lo moderno; addListener es el respaldo para WebViews
    // antiguas de Android y Safari < 14, que todavía usan varios clientes.
    if (typeof consulta.addEventListener === 'function') {
      consulta.addEventListener('change', alCambiar);
      return () => consulta.removeEventListener('change', alCambiar);
    }
    consulta.addListener(alCambiar);
    return () => consulta.removeListener(alCambiar);
  }, []);

  const temaAplicado = resolverTema(opcion, temaDelSistema);

  // Pintar es un efecto, nunca parte del render ni del inicializador.
  useEffect(() => {
    aplicarTema(temaAplicado);
  }, [temaAplicado]);

  const cambiarTema = useCallback((nueva: OpcionTema) => {
    if (!esOpcionValida(nueva)) return;
    opcionVigente = nueva;
    escuchas.forEach(avisar => avisar(nueva));
    try {
      localStorage.setItem(CLAVE_TEMA, nueva);
    } catch {
      // Almacenamiento lleno o bloqueado: el tema igual se aplica en esta
      // sesión, solo que no se recordará en el próximo arranque.
    }
  }, []);

  return { opcion, temaAplicado, temaDelSistema, cambiarTema };
}

export default useTema;
